/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, Text, View } from "@react-pdf/renderer";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EvidencePart } from "@interfaces/Interfaces";
import Autocomplete, { createFilterOptions } from "@mui/joy/Autocomplete";
import {
  findSupervisorEmailValueByInput,
  supervisorAutofillSelectionAtom,
  supervisorEmailsFromRoleAtom,
} from "@state/EvidenceState";
import { useRecoilValue, useSetRecoilState } from "recoil";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  onInputBlurred: () => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  isLocked?: boolean;
  pdfStyles: any;
  initialValue?: any;
  id: string;
};

function toEmailInputString(v: unknown): string {
  if (v === null || v === undefined || v === "") {
    return "";
  }
  return String(v);
}

/** PDF/display rendering runs outside RecoilRoot (@react-pdf/renderer); must not use Recoil hooks. */
const EmailInputStatic: React.FC<Props> = (props) => {
  if (props.isPDF) {
    return (
      <View>
        <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
        <Link
          style={props.pdfStyles.hyperlink}
          src={`mailto:${props.data}`}
        >
          {props.data}
        </Link>
      </View>
    );
  }

  return (
    <div className="readEvidenceBlock">
      <div className="readEvidenceHeader">{props.definition["Display Title"] ?? props.definition.Name}</div>
      <a
        href={`mailto:${props.data}`}
        target="_blank"
        className="readEvidenceText !text-cta-blue underline"
        rel="noreferrer"
      >
        <span className="!text-cta-blue underline">{props.data}</span>
      </a>
      <div className="mt-4 h-[1px] w-full bg-grey-30" />
    </div>
  );
};

const EmailInputInteractive: React.FC<Props> = (props) => {
  const [value, setValue] = useState<string>(() => toEmailInputString(props.initialValue));
  const [initialValueSet, setInitialValueSet] = useState<boolean>(false);
  const supervisorEmails = useRecoilValue(supervisorEmailsFromRoleAtom);
  const supervisorSelection = useRecoilValue(supervisorAutofillSelectionAtom);
  const setSupervisorAutofillSelection = useSetRecoilState(supervisorAutofillSelectionAtom);

  const emailOptions = useMemo(() => supervisorEmails?.map((s) => s.email).sort() ?? [], [supervisorEmails]);
  const filterOptions = createFilterOptions({
    matchFrom: "start",
    ignoreAccents: true,
  });

  const publishSupervisorSelectionForInput = useCallback(
    (input: string) => {
      const match = findSupervisorEmailValueByInput(supervisorEmails, input);
      setSupervisorAutofillSelection(match);
    },
    [supervisorEmails, setSupervisorAutofillSelection]
  );

  useEffect(() => {
    if (props.isLocked && !initialValueSet) {
      const normalized = toEmailInputString(props.initialValue);
      setValue(normalized);
      setInitialValueSet(true);
      props.onValueChanged && props.onValueChanged(normalized);
    }
  }, [props.initialValue, props.isLocked, props.onValueChanged, initialValueSet]);

  useEffect(() => {
    if (!props.initialValue?.length) {
      setValue("");
    }
  }, [props.id]);

  /** When another supervisor-autofill field picks a row (`SupervisorEmailValue.key`), mirror this field to that row's email. */
  useEffect(() => {
    if (!props.definition.EnableSupervisorAutofill || !supervisorSelection) {
      return;
    }
    const desired = supervisorSelection.email;
    if (desired !== value) {
      setValue(desired);
      props.onValueChanged && props.onValueChanged(desired);
    }
  }, [supervisorSelection?.key, props.definition.EnableSupervisorAutofill]);

  const autocompleteChanged = (_event: React.SyntheticEvent | null, newInputValue: string): void => {
    const _value = newInputValue ?? "";
    setValue(_value);
    props.onValueChanged && props.onValueChanged(_value);
    publishSupervisorSelectionForInput(_value);
  };

  const inputChanged = (event: any): void => {
    const _value = event.target.value;

    setValue(_value);
    props.onValueChanged && props.onValueChanged(_value);
  };

  const inputBlurred = (): void => {
    props.onInputBlurred && props.onInputBlurred();
  };

  if (props.definition.EnableSupervisorAutofill) {
    return (
      <Autocomplete
        options={emailOptions}
        filterOptions={filterOptions}
        freeSolo
        placeholder={props.definition?.["Placeholder Text"] ?? "Enter a value..."}
        autoComplete={false}
        value={value}
        getOptionLabel={(option) => String(option ?? "")}
        onInputChange={autocompleteChanged}
        onBlur={inputBlurred}
        variant="plain"
        sx={{
          "--Textarea-placeholderOpacity": 1,
          backgroundColor: "white",
          minHeight: "28px",
          color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
          paddingLeft: 0,
          fontSize: "20px",
          fontWeight: "600",
          "@media (max-width: 500px)": {
            fontSize: "17px",
          },
          ":hover": {
            color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
          },
        }}
      />
    );
  }

  return (
    <>
      <input
        className="reflectionTagsInput"
        placeholder={props.definition?.["Placeholder Text"] || "Enter a value..."}
        autoComplete="off"
        value={value}
        onChange={inputChanged}
        onBlur={inputBlurred}
        type="email"
      />
    </>
  );
};

const EmailInput: React.FC<Props> = (props) => {
  if (props.isPDF || props.isDisplay) {
    return <EmailInputStatic {...props} />;
  }
  return <EmailInputInteractive {...props} />;
};

export default EmailInput;
